import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { R, TypedVariables } from 'apollo-angular/types';
import { ApolloError, ApolloQueryResult, MutationOptions, QueryOptions, WatchQueryOptions } from 'apollo-client';
import { Predicate } from 'core-js';
import { ToastService } from 'ng-uikit-pro-standard';
import { EMPTY, Subject, throwError } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { BloqueoModuloError } from '../object/bloqueo-modulo-error.class';
import { BloqueoModuloService } from './bloqueo-modulo.service';

@Injectable({
  providedIn: 'root'
})
export class GraphQLClient {

  private apollo: Apollo;

  private destroy$ = new Subject<void>();

  constructor(
    apollo: Apollo, 
    public router: Router,
    private bloqueoModuloService: BloqueoModuloService,
    public toastService: ToastService,
  ) {
    this.router.events.subscribe(() => this.destroy$.next());
    this.apollo = apollo;
  }

  private extraerNotificaciones(data) {

    let obj = data;
    let stack = '';
    const retorno = [];

    while (obj != null) {
      // tslint:disable-next-line:forin
      for (const property in obj) {
        if (obj == null) {
          break;
        }

        if (obj.hasOwnProperty(property)) {
          if (typeof obj[property] === 'object') {
            stack = stack + '.' + property;
            obj = obj[property];
          } else {
            if (property === '__typename') {
              const valor: string = obj[property];
              if (valor.startsWith('Notificacion')) {
                retorno.push(obj);
                break;
              }
            }
          }
        }
      }
    }
    return retorno;
  }

  private alExito(data: ApolloQueryResult<{}>, onSuccess: Predicate<ApolloQueryResult<{}>>): void {

    onSuccess(data);
    return;
  }

  private alError(error: Error): void {
    if (error instanceof ApolloError) {
      const httpError: HttpErrorResponse = <HttpErrorResponse>error.networkError;
      if (httpError.status === 401) {
        alert('¡No autorizado!. Por favor inicia sesión nuevamente...');
        //this.router.navigate(['/login']); 
      }
      if (httpError.status === 403) {
        alert('¡No tienes permisos para realizar esta acción!');
        this.router.navigate(['/login']);
      }
    } else {
      alert('Ha ocurrido un error, porfavor intentelo mas tarde!');
    }
  }

  watchQuery<T, V = R>(options: WatchQueryOptions & TypedVariables<V>, onSuccess: Predicate<T>): void {
    throw new Error('No implementado aún...!');
  }

  query<T, V = R>(options: QueryOptions & TypedVariables<V>, onSuccess: Predicate<ApolloQueryResult<{}>>): void {
    try {
      const self = this;

      const codigoModuloBloqueado = this.bloqueoModuloService.getCodigoModuloBloqueado();

      // Si el usuario tiene un bloqueo activo
      if (codigoModuloBloqueado) {
        const inputParams = {
          codigoModulo: codigoModuloBloqueado,
          rutUsuario: this.bloqueoModuloService.getRutUsuario(),
        }
        this.bloqueoModuloService.verificarBloqueoActualActivo(inputParams).pipe(
          takeUntil(this.destroy$),
          switchMap(bloqueo => {
            if (!this.esMismoModulo(codigoModuloBloqueado)) {
              return EMPTY; // navegación cambió de módulo; ignorar respuesta tardía
            }
            if (bloqueo.exito) {
              return this.apollo.query<T, V>(options);
            }else{
              return throwError(new BloqueoModuloError(bloqueo.mensajeError));
            }
          })
        ).subscribe(function (data: ApolloQueryResult<{}>) {
          self.alExito(data, onSuccess);
        }, mierror => {
          console.log({mierror});
          if (mierror instanceof ApolloError) {
            const httpError: HttpErrorResponse = <HttpErrorResponse>mierror.networkError;
            if (httpError.status === 401) {
              alert('¡No autorizado!. Por favor inicia sesión nuevamente...');
              this.router.navigate(['/login']);
            }
            if (httpError.status === 403) {
              alert('¡No tienes permisos para realizar esta acción!');
              this.router.navigate(['/login']);
            }
          } else if (mierror instanceof BloqueoModuloError) {
            this.handleLockInvalidated(mierror);
          }else {
            alert('Ha ocurrido un error, porfavor intentelo mas tarde!');
          }
        });
      }else{ // Si el usuario no tiene un bloqueo activo
        this.apollo
          .query(options)
          .subscribe(function (data: ApolloQueryResult<{}>) {
            self.alExito(data, onSuccess);
          }, mierror => {
            if (mierror instanceof ApolloError) {
              const httpError: HttpErrorResponse = <HttpErrorResponse>mierror.networkError;
              if (httpError.status === 401) {
                alert('¡No autorizado!. Por favor inicia sesión nuevamente...');
                this.router.navigate(['/login']);
              }
              if (httpError.status === 403) {
                alert('¡No tienes permisos para realizar esta acción!');
                this.router.navigate(['/login']);
              }
            } else {
              alert('Ha ocurrido un error, porfavor intentelo mas tarde!');
            }
          }
        );
      }
    } catch (e) {
      this.alError(e);
    }
  }

  mutate<T, V = R>(options: MutationOptions & TypedVariables<V>, onSuccess: Predicate<ApolloQueryResult<{}>>, controllErrors: boolean = true): void {
    const codigoModuloBloqueado = this.bloqueoModuloService.getCodigoModuloBloqueado();
    const self = this;
    
    if (codigoModuloBloqueado) {
      const inputParams = {
        codigoModulo: codigoModuloBloqueado,
        rutUsuario: this.bloqueoModuloService.getRutUsuario(),
      }
      this.bloqueoModuloService.verificarBloqueoActualActivo(inputParams).pipe(
        takeUntil(this.destroy$),
        switchMap(bloqueo => {
          if (!this.esMismoModulo(codigoModuloBloqueado)) {
            return EMPTY; // navegación cambió de módulo; ignorar respuesta tardía
          }
          if (bloqueo.exito) {
            return this.apollo.mutate<T, V>(options);
          }else{
            return throwError(new BloqueoModuloError(bloqueo.mensajeError));
          }
        })
      ).subscribe(function (data: ApolloQueryResult<T>) {
        self.alExito(data, onSuccess);
      }, mierror => {
        if (mierror instanceof ApolloError) {
          const httpError: HttpErrorResponse = <HttpErrorResponse>mierror.networkError;
          if (httpError.status === 401) {
            alert('¡No autorizado!. Por favor inicia sesión nuevamente...');
            this.router.navigate(['/login']);
          }
          if (httpError.status === 403) {
            alert('¡No tienes permisos para realizar esta acción!');
            this.router.navigate(['/login']);
          }
        } else if (mierror instanceof BloqueoModuloError) {
          console.log('BLOQUEO MODULO ERROR', mierror);
          this.handleLockInvalidated(mierror);
        } else {
          alert('Ha ocurrido un error, porfavor intentelo mas tarde!');
        }
      });
    } else {
      try {  
        this.apollo
          .mutate(options)
          .subscribe(function (data: ApolloQueryResult<{}>) {
            self.alExito(data, onSuccess);
          }, mierror => {
            if (mierror instanceof ApolloError) {
              const httpError: HttpErrorResponse = <HttpErrorResponse>mierror.networkError;
              if (httpError.status === 401) {
                alert('¡No autorizado!. Por favor inicia sesión nuevamente...');
                this.router.navigate(['/login']);
              }
              if (httpError.status === 403) {
                alert('¡No tienes permisos para realizar esta acción!');
                this.router.navigate(['/login']);
              }
            } else {
              alert('Ha ocurrido un error, porfavor intentelo mas tarde!');
            }
          });
      } catch (e) {
        this.alError(e);
      }
    }
  }

  private handleLockInvalidated(mierror: BloqueoModuloError): void {
    this.toastService.clear();
    this.toastService.error(mierror.message, mierror.name);
    this.bloqueoModuloService.forzarLiberacion();
    this.router.navigate(['/']);
  }

  private esMismoModulo(expectedModulo: number | null): boolean {
    return this.bloqueoModuloService.getCodigoModuloBloqueado() === expectedModulo;
  }
}
